<template>
  <b-form>
    <b-form-row>
      <h3 class="mx-3">
        <template v-if="!dealid || !deal.name">New Deal</template>
        <template v-else>{{ deal.name }}</template>
      </h3>

      <template v-if="dealid">
        <toggle-button
          v-model="deal.enabled"
          @change="save()"
          :sync="true"
          :labels="{ checked: 'Enabled', unchecked: 'Disabled' }"
          :height="28"
          :width="80"
          class="align-middle mt-1"
          :color="{ checked: '#2bd879', unchecked: '#F1516C' }">
        </toggle-button>
      </template>
    </b-form-row>
    <b-card class="my-4 w-100" title="General Settings">
      <b-form-row class="mt-4">
        <b-col md="4">
          <b-form-group label="Deal name">
            <b-form-input v-model="deal.name" :disabled="!!dealid" />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group label="Deal ID">
            <b-form-input v-model="deal.id" :disabled="!!dealid" />
          </b-form-group>
        </b-col>
        <b-col xl="5">
          <b-row>
            <b-col>
              <b-form-group label="Flight Dates">
                <!-- must mount datepicker with loaded date info -->
                <date-picker v-if="(dealid && deal.start_time) || dealid === undefined"
                             @change="dateChange" :date="dateSettings" :hide-interval="true" :limit-today="false"></date-picker>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col lg="4">
          <b-form-group
            label="Deal Type"
            description="Type and priority of this deal, permanent">
            <b-form-select v-model="deal.type" @change="typeChange">
              <option :disabled='(deal.wdsps && deal.wdsps["23"] && dealid) || (!isSuperAdmin && dealid && deal.type.indexOf("PRIVATE") > -1)' value="OPEN">Open</option>
              <option :disabled='(deal.wdsps && deal.wdsps["23"] && dealid) || (!isSuperAdmin && dealid && deal.type.indexOf("PRIVATE") > -1)' value="OPEN_PREFERRED">Open Preferred</option>
              <option :disabled='(deal.wdsps && deal.wdsps["23"] && dealid) || (!isSuperAdmin && dealid && deal.type.indexOf("OPEN") > -1)' value="PRIVATE">Private</option>
              <option :disabled='(deal.wdsps && deal.wdsps["23"] && dealid) || (!isSuperAdmin && dealid && deal.type.indexOf("OPEN") > -1)' value="PRIVATE_FIXED">Private Fixed</option>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group :label="deal.type === 'PRIVATE_FIXED' ? 'Fixed Price' : 'Override floor'"
                        :description="deal.type === 'PRIVATE_FIXED' ? 'Agreed price for this deal. Sent to DSP as entered' : '0 inherits bidfloor if not  set'">
            <b-form-input v-model="deal.floor" type="number" />
          </b-form-group>
        </b-col>

        <b-col md="2" v-if="deal.type.indexOf('PRIVATE') > -1">
          <b-form-group label="Fixed Margin"
                        description="0 inherits pub revshare">
            <b-form-input v-model="deal.revshare" type="number" />
          </b-form-group>
        </b-col>

        <b-col v-if="deal.type.indexOf('PRIVATE') > -1">
          <b-form-group
            label="Category Override"
            description="Override request bcat, careful!">
            <b-form-select v-model="deal.cat_override">
              <b-select-option :value="null">NONE</b-select-option>
              <b-select-option v-for="cat in cats" :key="cat.code" :value="cat.code">
                {{ cat.label }}
              </b-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-btn @click="save()" class="mt-4" variant="primary">Save</b-btn>
    </b-card>

    <b-card class="my-4 w-100" v-if="!deal.skip_send_to_dv_api && deal.wdsps && deal.wdsps['23'] && deal.wdsps['23'].length > 0 &&  (!deal.dv_deal_open || !deal.dv_deal_open.auction_package)">
      <b-card-title>
        <b-row>
          <b-col>
            DV360 Buyer Specific Deal Sync Settings
          </b-col>
          <b-col v-if="deal.dv_deal_private.order && deal.dv_deal_private.order.status">
            <b-badge class="float-right">
              <b-badge v-if="deal.dv_deal_private.order.status == 'PENDING_ACCEPTANCE'">PENDING</b-badge>
              <b-badge variant="success" v-else-if="deal.dv_deal_private.order.status == 'ACCEPTED'">ACCEPTED</b-badge>
              <b-badge variant="primary" v-else-if="deal.dv_deal_private.order.status == 'NEGOTIATING'">NEGOTIATING</b-badge>
              <b-badge variant="danger" v-else-if="deal.dv_deal_private.order.status == 'ARCHIVED'">ARCHIVED</b-badge>
              <b-badge variant="error" v-else-if="deal.dv_deal_private.order.status == 'CANCELLED'">CANCELLED</b-badge>
            </b-badge>
          </b-col>
        </b-row>
      </b-card-title>
      <b-alert variant="warning" show>
        This will go to dv360 buyer specific Sync API which is sent
        directly to the buyer(s) for approval
      </b-alert>
      <b-row>
        <b-col>
          <b-form-group label="Publisher Description" description="Public descritpion buyers will see of included inventory">
            <b-form-input required v-model="deal.dv_deal_private.publisher" maxlength="100" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-row>
        <b-col md="5">
          <b-form-row>
            <b-col>
              <b-form-group label="Format">
                <b-form-select v-model="deal.dv_deal_private.format" :disabled="deal.dv_deal_private.order && !!deal.dv_deal_private.order.name">
                  <b-form-select-option value="CREATIVE_TYPE_DISPLAY">Display</b-form-select-option>
                  <b-form-select-option value="CREATIVE_TYPE_VIDEO">Video</b-form-select-option>
                  <b-form-select-option value="CREATIVE_TYPE_NATIVE">Native</b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col v-if="deal.dv_deal_private.format === 'CREATIVE_TYPE_DISPLAY' && deal.type == 'PRIVATE_FIXED'">
              <b-form-group label="Width">
                <b-form-input :disabled="deal.dv_deal_private.order && !!deal.dv_deal_private.order.name"
                  required v-model.number="deal.dv_deal_private.banner_w"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col v-if="deal.dv_deal_private.format === 'CREATIVE_TYPE_DISPLAY' && deal.type == 'PRIVATE_FIXED'">
              <b-form-group label="Height">
                <b-form-input :disabled="deal.dv_deal_private.order && !!deal.dv_deal_private.order.name"
                  required v-model.number="deal.dv_deal_private.banner_h"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col v-if="deal.dv_deal_private.format === 'CREATIVE_TYPE_VIDEO' && deal.type == 'PRIVATE_FIXED'">
              <b-form-group label="Max Duration">
                <b-form-input :disabled="deal.dv_deal_private.order && !!deal.dv_deal_private.order.name"
                  required v-model.number="deal.dv_deal_private.video_duration"></b-form-input>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-col>
        <b-col md="2">
          <b-form-group label="Media Type">
            <b-form-select v-model="deal.dv_deal_private.medium" :disabled="deal.dv_deal_private.order && !!deal.dv_deal_private.order.name">
              <b-form-select-option value="DIGITAL">DIGITAL</b-form-select-option>
              <b-form-select-option value="TV">TV</b-form-select-option>
              <b-form-select-option value="DOOH">DOOH</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="2" v-if="deal.type === 'PRIVATE_FIXED'">
          <b-form-group label="Guaranteed">
            <b-checkbox :disabled="deal.dv_deal_private.order && !!deal.dv_deal_private.order.name"
              v-model="deal.dv_deal_private.reserved">
              <small>Reserved</small>
            </b-checkbox>
          </b-form-group>
        </b-col>
        <b-col md="2" v-if="deal.type === 'PRIVATE_FIXED' && deal.dv_deal_private.reserved">
          <b-form-group label="Reserved Imps" description="Count of guaranteed impressions">
            <b-form-input v-model.number="deal.dv_deal_private.imps" type="number" />
          </b-form-group>
        </b-col>
      </b-form-row>
    </b-card>

    <!-- we changed behavior here to send even open deals w/ wseat to buyer specific API.
      but we want to continue showing deals already sent to the auction package API with the appropriate API ui section here -->
    <b-card class="my-4 w-100" v-if="!deal.skip_send_to_dv_api && deal.wdsps && deal.wdsps['23'] && (!deal.wdsps['23'].length || (deal.dv_deal_open && deal.dv_deal_open.auction_package))
      && deal.type.indexOf('OPEN') > - 1">
      <b-card-title>
        <b-row>
          <b-col>
            DV360 Auction Package Settings
          </b-col>
          <b-col v-if="deal.dv_deal_open && deal.dv_deal_open.auction_package">
            <b-badge class="float-right">
              <b-badge variant="danger" v-if="deal.dv_deal_open.status == 'DISCOVERY_OBJECT_STATUS_UNSPECIFIED'">UNKNOWN</b-badge>
              <b-badge variant="success" v-else-if="deal.dv_deal_open.status == 'DISCOVERY_OBJECT_STATUS_ACTIVE'">ACTIVE</b-badge>
              <b-badge variant="primary" v-else-if="deal.dv_deal_open.status == 'DISCOVERY_OBJECT_STATUS_PAUSED'">PAUSED</b-badge>
              <b-badge variant="danger" v-else-if="deal.dv_deal_open.status == 'DISCOVERY_OBJECT_STATUS_REMOVED'">REMOVED</b-badge>
              <b-badge variant="error" v-else-if="deal.dv_deal_open.status == 'DISCOVERY_OBJECT_STATUS_ARCHIVED'">ARCHIVED</b-badge>
            </b-badge>
          </b-col>
        </b-row>
      </b-card-title>
      <b-alert variant="warning" show>
        This will go to dv360 Auction Package API, which is open to any buyer (unless a historic open deal with wseat prior to API changes)
      </b-alert>
      <b-row>
        <b-col>
          <b-form-group label="Package Description" description="Public description buyers will see of this auction package">
            <b-form-input v-model="deal.dv_deal_open.description" maxlength="100" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-row>
        <b-col md="5">
          <b-form-row>
            <b-col>
              <b-form-group label="Format">
                <b-form-select v-model="deal.dv_deal_open.format" :disabled="deal.dv_deal_open && !!deal.dv_deal_open.auction_package">
                  <b-form-select-option value="DEAL_FORMAT_DISPLAY">Display</b-form-select-option>
                  <b-form-select-option value="DEAL_FORMAT_VIDEO">Video</b-form-select-option>
                  <b-form-select-option value="DEAL_FORMAT_AUDIO">Native</b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-col>
        <b-col md="2">
          <b-form-group label="Media Type">
            <b-form-select v-model="deal.dv_deal_open.medium" :disabled="deal.dv_deal_open && !!deal.dv_deal_open.auction_package">
              <b-form-select-option value="MEDIUM_TYPE_DIGITAL">DIGITAL</b-form-select-option>
              <b-form-select-option value="MEDIUM_TYPE_TV">TV</b-form-select-option>
              <b-form-select-option value="MEDIUM_TYPE_OOH">DOOH</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-form-row>
    </b-card>

    <b-card class="my-4 w-100" title="DSP Targeting">
      <b-alert show class="alert-medium">
        <span v-if="deal.type === 'OPEN' || deal.type === 'OPEN_PREFERRED'">
          Selecting specific DSPs to receive this deal when Open is optional.
          By default all DSPs will receive this auction and deal. If specific DSPs
          are selected, only they will receive this deal sent to them but other
          bidders will still receive the bid request auction, just w/o this deal present.
        </span>
        <span v-if="deal.type === 'PRIVATE'">
          A private deal is an invite only group of buyers and must have bidder(s) selected to receive deals
          in an auction. For XSP, most requests with private deals will also send a duplicated
          impression object to allow open auction spend back fill, so private deals will not
          prevent open auction spend.
        </span>
        <span v-if="deal.type === 'PRIVATE_FIXED'">
          This is a preferred or guaranteed deal in which only <b>one buyer can receive it at a fixed price</b>.
        </span>
      </b-alert>

      <b-form-row>
        <b-col md="12">
          <b-form-group
            label="Enabled Bidders"
            description="Restrict deal to selected DSPs, Open deals go to all DSPs by default">
            <mulselect
              variant="light-muted"
              :options="bidderOptions"
              option-value-field="company_id"
              option-label-field="name"
              :limit="deal.type === 'PRIVATE_FIXED' ? 1 : 10"
              :values="wbidders">
            </mulselect>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row  v-if="deal.wdsps && deal.wdsps['23']">
        <b-form-group
          label="Register deal with DV360 API"
          description="Disable create in DV360 deals system if X-15 already has registered this deal ID"
          >
          <b-col>
            <toggle-button
              :disabled="!!dealid"
              v-model="deal.skip_send_to_dv_api"
              :sync="true"
              :labels="{ checked: 'Skip', unchecked: 'Send' }"
              :height="28"
              :width="80"
              class="align-middle mt-1"
              :color="{ unchecked: '#2bd879', checked: '#F1516C' }">
            </toggle-button>
          </b-col>
        </b-form-group>
      </b-form-row>

      <div>
        <h6 class="text-secondary mt-5">
          DSP Seat Mapping
          <b-badge variant="secondary" v-if="deal.type === 'OPEN' || deal.type === 'OPEN_PREFERRED'">Optional</b-badge>
          <b-badge variant="primary" v-if="deal.type !== 'OPEN' && deal.type !== 'OPEN_PREFERRED'">Required</b-badge>
        </h6>
        <b-alert :show="!wbidders.length">Please enable bidder(s)</b-alert>

        <b-form-row v-for="bidderId of Object.keys(deal.wdsps)" :key="bidderId">
          <b-col>
            <b-form-group :label="'Edit ' + (bidderId !== '26' ? 'seat' : 'bsw bidder id') + ' list for ' + bidderName(bidderId)">
              <string-list-editor :values="deal.wdsps[bidderId]" :required="deal.type !== 'OPEN' && deal.type !== 'OPEN_PREFERRED'"
                                  placeholder="Enter seat id(s)"></string-list-editor>
            </b-form-group>
          </b-col>
        </b-form-row>
      </div>

      <!-- enable the ext.buyer_wseat extension for BidSwitch if BSW is enabled in DSP list
        AND there are "seats", actually bsw bidder IDs, mapped for bsw
      -->
      <div v-if="deal.wdsps && deal.wdsps['26'] && deal.wdsps['26'].length"
        class="border p-3 pt-0 mt-5 bg-light">
        <h6 class="text-secondary mt-5">
          BidSwitch deal extension seat mapping.
        </h6>

        <b-form-row>
          <b-col>
            <b-form-group label="Edit true wseat values for BidSwitch auctions to send to all BSW bidder(s) enabled for this deal in the seat mapping above">
              <string-list-editor :values="deal.bsw_seats" placeholder="Enter deal.ext.buyer_wseat id(s)"></string-list-editor>
            </b-form-group>
          </b-col>
        </b-form-row>
      </div>

    </b-card>

    <b-card class="my-4 w-100" title="Help Guide">
      <b-alert show>
        The floor price setting of 0 will by default inherit the open auction price and if set, will override the open
        auction price if the deal floor is set higher. For private fixed, the floor price is the fixed negotiated deal price
        and is <b>sent directly to the bidder as set here without markup.</b> Revshare margin is applied after the bid is
        received and then forwarded to the publisher.
      </b-alert>
      <b-row v-for="type in typeHelps" :key="type.label">
        <b-col lg="2">
          <b-badge variant="primary">
            {{ type.label }}
          </b-badge>
        </b-col>
        <b-col>
          {{ type.desc }}
        </b-col>
      </b-row>
    </b-card>

    <b-card class="mt-4" v-if="dealid">
      <h5 class="mb-4">Targeting Groups</h5>
      <b-row v-if="deal && deal.targeting && deal.targeting.length">
        <b-list-group-item v-for="(target, index) in deal.targeting" :key="target.name"
                           class="w-100 d-flex justify-content-between align-items-center">
          {{ target.name }}
          <div class="float-right">
            <b-btn :to="{ name: 'AdminDealTargeting', params: { dealid: deal.id, targetingidx: index } }" variant="outline-secondary" class="">Edit</b-btn>
            <b-btn @click="deleteGroup(index)" variant="outline-danger" class="float-right text-danger ml-3">Delete</b-btn>
          </div>
        </b-list-group-item>
      </b-row>
      <b-row v-else>
        <div class="text-muted p-3">No targeting groups yet</div>
      </b-row>
      <b-btn :to="{ name: 'AdminDealTargeting', params: { dealid: deal.id } }"
             variant="primary" class="mt-4">
        + Add Targeting Group
      </b-btn>
    </b-card>
  </b-form>
</template>

<script>
import Mulselect from '@/components/Mulselect'
import StringListEditor from "@/components/StringListEditor"
import {DemandApi} from "../api";
import { ExchangeApi } from '@/exchange/api'
import {MiscApi} from "../../api";
import ManySelector from "../../components/ManySelector.vue";
import DatePicker from "../../components/DatePicker.vue";
import auth from "../../auth";
import iab from '@/assets/iab';

export default {
  name: 'DealSettings',
  props: {
    dealid: { required: false }
  },
  mounted () {
    this.getExchangeList()
    this.getCountryList()
    this.getBidders()

    if (this.dealid)
      this.getDeal(this.dealid)
  },
  data () {
    return {
      isSuperAdmin: auth.isSuperAdmin(),
      BRAND,
      deal: {
        skip_send_to_dv_api: false,
        dv_deal_private: {
          medium: 'DIGITAL',
          imps: 0,
          format: 'CREATIVE_TYPE_DISPLAY',
          reserved: false
        },
        dv_deal_open: {
          medium: 'MEDIUM_TYPE_DIGITAL',
          format: 'DEAL_FORMAT_DISPLAY'
        },
        enabled: false,
        name: null,
        id: this.dealid,
        type: 'OPEN',
        start_time: null,
        end_time: null,
        floor: 0,
        revshare: 0,
        targeting: [],
        wdsps: {},
        bsw_seats: [],
        exchanges: [],
        countries: [],
        device: {
          platforms: [],
          types: {}
        },
        video: {
          livestream: 'ANY',
          languages: [],
          ratings: []
        },
        formats: {
          banner: false,
          video: false,
          native: false
        },
        pub_ids: [],
        bundles: [],
        cat_override: null
      },
      wbidders: [], // tmp storage for enabled bidder list
      wseats: {}, // tmp storage of enabled list of seat IDs by individual bidder ID from wbidders
      bidderOptions: [],
      exchangeOptions: [],
      countryOptions: [],
      platformOptions: [
        { value: "android", text: "Android"},
        { value: "ios", text: "iOS"},
        { value: "windows", text: "Windows"},
        { value: "osx", text: "Mac OS X"},
        { value: "roku", text: "Roku"},
        { value: "apple_tv", text: "Apple TV"},
        { value: "web_os", text: "Web OS"},
        { value: "tizen", text: "Tizen"},
        { value: "os_other", text: "All Other"},
      ],
      langOptions: [
        { value: "en", text: "English" },
        { value: "es", text: "Spanish" },
        { value: "fr", text: "French" },
        { value: "de", text: "German" },
        { value: "pt", text: "Portuguese" },
        { value: "it", text: "Italian" },
      ],
      videoRatingOptions: [
        { value: "G", text: "(Movie) G General Audiences" },
        { value: "PG", text: "(Movie) PG Parental Guidance" },
        { value: "PG13", text: "(Movie) PG-13 Parents Cautioned" },
        { value: "R", text: "(Movie) R restricted" },
        { value: "NC17", text: "(Movie) NC-17 Adult" },
        { value: "TVG", text: "(TV) TV-G General" },
        { value: "TVPG", text: "(TV) TV-PG Parental Guidance" },
        { value: "TV14", text: "(TV) TV-14 Parents Cautioned" },
        { value: "TVMA", text: "(TV) TV-MA Mature" },
      ],
      dateSettings: {
        start: null,
        end: null
      },
      typeHelps: [
        { label: "Open", desc: "Public auction deal any bidder can participate in. Highest price bid regardless of deal/no deal wins." },
        { label: "Open Preferred", desc: "Public auction deal competes with open auction, but deal bids get preference over any open auction bid regardless of price." },
        { label: "Private", desc: "Private list of bidders, auction ONLY goes to these bidders. Competes on price." },
        { label: "Private Fixed", desc: "Preferred or PG. Goes to one buyer with a prenegotiated price. Bidfloor = deal price." },
      ]
    }
  },
  methods: {
    save () {
      let d = this.deal

      if (!d.name || d.name.length < 4) {
        return this.$notify.error("Please fill in name field")
      }

      if (!d.id || d.id.indexOf(" ") > -1) {
        return this.$notify.error("Please fill in deal ID as a single deal identifier without spaces")
      }

      if (!d.start_time || !d.end_time) {
        return this.$notify.error('Please select start and end dates')
      }

      if (d.wdsps && d.wdsps["23"] != undefined && !this.isSuperAdmin)
        return this.$notify.error('Lacking super admin perms for DV deal sync')

      DemandApi.dealSave(d).then((deal) => {
        this.$notify.success("Deal saved")

        if (this.dealid) {
          this.$router.push({name: 'AdminPublicDeals'})
        } else {
          this.$router.push({ name: 'AdminDealTargeting', params: {
              dealid: deal.id
            }})
        }
      })
    },
    deleteGroup(idx) {
      this.deal.targeting.splice(idx, 1)
    },
    dateChange () {
      this.deal.start_time = this.dateSettings.start
      this.deal.end_time = this.dateSettings.end
    },
    getExchangeList() {
      ExchangeApi.list().then((data) => {
        this.exchangeOptions.push(...data)
      })
    },
    getCountryList() {
      MiscApi.getCountryList().then((data) => {
        let set = new Set(data.countries)
        this.countryOptions.push(...set.keys())
      }, (err) => {
        console.warn(err)
      })
    },
    getDeal(dealId) {
      return DemandApi.dealGet(dealId).then(deal => {
        this.dateSettings.start = deal.start_time
        this.dateSettings.end = deal.end_time

        this.$set(this, 'deal', Object.assign({}, this.deal, deal))

        if (this.deal.wdsps) {
          for (let dspId of Object.keys(this.deal.wdsps)) {
            this.wbidders.push(dspId)
          }
        }
      }).catch(e => {
        this.$notify.error('Failed loading deal: ' + e)
      })
    },
    getBidders () {
      return DemandApi.list().then(list => {
        if (list && list.list)
          this.bidderOptions = list.list
        else
          this.$notify.error("Failed listing bidders")
      })
    },
    bidderName (id) {
      for (let bidder of this.bidderOptions) {
        if (bidder.company_id === id)
          return bidder.name
      }

      return 'Unknown'
    },
    typeChange (type) {
      if (type === 'PRIVATE_FIXED' && this.wbidders)
        this.wbidders.splice(1)
    }
  },
  watch: {
    wbidders () {
      if (Object.keys(this.deal.wdsps).length > this.wbidders.length) {
        for (let bId of Object.keys(this.deal.wdsps)) {
          if (this.wbidders.indexOf(bId) == -1) {
            console.log('removed bidder ' + bId)
            delete this.deal.wdsps[bId]
          }
        }
      } else if (this.wbidders.length > Object.keys(this.deal.wdsps).length) {
        for (let bId of this.wbidders) {
          if (!this.deal.wdsps[bId]) {
            this.$set(this.deal.wdsps, bId, [])
            console.log('Added wb ' + bId)
          }
        }
      }
    }
  },
  computed: {
    cats () {
      const list = []
      Object.keys(iab).forEach(code => {
        list.push({ code, label: (code + ' ' + iab[code]) })
      })
      return list
    }
  },
  components: {
    DatePicker,
    Mulselect,
    StringListEditor,
    ManySelector
  }
}
</script>



// WEBPACK FOOTER //
// src/demand/pages/DealSettings.vue